import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMission } from "../../../../providers/MissionProvider";
import { useNavigate, useParams } from "react-router-dom";
import { useEntityField, Defaults, useAuth, Task } from "@emberly/zenith-client";
import { ActivityEnums, MissionEnums, MissionEnumsLists } from "../../../../common/constants";
import { SetAllTasksCreated } from "../../../../common/mission";
import MissionActivityLog from "./MissionActivityLog";
import MissionRequestReceivedChip from "./MissionRequestReceivedChip";
import ResponsiveSubPage from "../../../common/core/ResponsiveSubPage";
import { Grid, Skeleton, Stack } from "@mui/material";
import CompleteMissionDialog from "./CompleteMissionDialog";
import { useStation } from "../../../../providers/StationProvider";
import { TimeView } from "../../../common/core/Time";
import config from "../../../../config";
import { PictureAsPdfOutlined as PDFIcon, SendOutlined as SendIcon } from "@mui/icons-material";
import PDFExportDialog from "./PDFExportDialog";
import ConfirmDialog from "../../../common/inputs/ConfirmDialog";
import CreateMissionRequestDialog from "../../missionrequests/missionrequest/CreateMissionRequestDialog";
import MissionRequestSentChip from "./MissionRequestSentChip";
import MissionRequestDialog from "../../missionrequests/missionrequest/MissionRequestDialog";
import { useDevice } from "../../../../providers/DeviceProvider";

const { State } = MissionEnums;

export default function MissionLayout(props) {
  const { t } = useTranslation();
  const { mission, deleteMission, hasUpdate, updateMissionField, logMissionEvent } = useMission(); // TODO
  const navigate = useNavigate();
  const { taskNumber, missionId } = useParams();
  const { user } = useAuth();
  const { permissions } = useStation();
  const { slim } = useDevice();

  const missionState = useEntityField("state", Defaults.Enum);
  const state = missionState.value;
  const setState = missionState.onChange;
  const backPath = !!taskNumber ? "../../" : "../";

  const [pdfDialog, setPdfDialog] = useState(false);
  const onClosePdfDialog = useCallback(() => setPdfDialog(false), []);

  const [requestDialog, setRequestDialog] = useState(false);
  const onCloseMissionRequestDialog = useCallback(() => setRequestDialog(false), []);
  const [selectedRequestId, setSelectedRequestId] = useState(null);
  const onOpenCreatedRequest = useCallback((id) => {
    setRequestDialog(null);
    setSelectedRequestId(id);
  }, [])

  const onCloseSelectedRequest = useCallback(() => setSelectedRequestId(null), []);


  const [completeDialog, setCompleteDialog] = useState(false);

  const [cancelMissionTask, setCancelMissionTask] = useState(null);

  const onPrimaryButton = useCallback(async () => {
    switch (state) {

      case State.Draft:
        await deleteMission();
        logMissionEvent(ActivityEnums.Type.MissionDeleted);
        navigate(backPath, { relative: "path" });
        break;

      case State.Created:

        try {
          const t = new Task();
          setCancelMissionTask(t);

          await t.wait();

          setState(null, State.Cancelled);
          logMissionEvent(ActivityEnums.Type.MissionCancelled);

        } catch {
          console.log("cancelled");
        } finally {
          setCancelMissionTask(null);
        }

        break;

      default:
        console.log("unknown state");
    }

  }, [state, backPath, deleteMission, setState, logMissionEvent, navigate]);

  const onSecondaryButton = useCallback(async () => {
    switch (state) {

      case State.Draft:
      case State.Cancelled:
        navigate(`/activemissions/${missionId}`, { relative: "path" });
      case State.Completed:
        setState(null, State.Created);
        SetAllTasksCreated(user, mission, updateMissionField);
        logMissionEvent(ActivityEnums.Type.MissionCreated);
        break;

      case State.Created:
        setCompleteDialog(true);
        break;

      default:
        console.log("unknown state");
    }

  }, [state, setState, missionId, updateMissionField, user, mission, logMissionEvent, navigate]);


  const onCancelComplete = useCallback(() => setCompleteDialog(false), []);

  const onComplete = useCallback(() => {
    setState(null, State.Completed);
    logMissionEvent(ActivityEnums.Type.MissionCompleted);
    setCompleteDialog(false);
  }, [setState, logMissionEvent]);


  const menuItems = useMemo(() => [
    {
      label: t("mission:exportToPDF"),
      icon: <PDFIcon />,
      onClick: () => setPdfDialog(true),
    },
    {
      label: t("mission:createRequest"),
      icon: <SendIcon />,
      onClick: () => setRequestDialog(true),
      disabled: state !== MissionEnums.State.Created || !permissions.canSendMissionRequest
    }
  ], [t, state, permissions]);

  return (
    <ResponsiveSubPage
      title={`${t("mission:mission")} ${mission?.number} ${hasUpdate ? "*" : ""}`}
      caption={<TimeView time={mission?.created} />}
      stateTag={state !== 0 ? t(getStateText(state)) : <Skeleton variant="rounded" width="37px" />}
      stateTagColor={state !== 0 ? getStateColor(state) : undefined}
      primaryAction={state === State.Draft ? t("mission:deleteDraft") : state === State.Created ? t("mission:cancelMission") : undefined}
      onPrimaryAction={(state === State.Draft || state === State.Created) ? onPrimaryButton : undefined}
      secondaryAction={state === State.Draft ? t("mission:createMission") : state === State.Created ? (permissions.canCompleteMission ? t("mission:completeMission") : null) : t("mission:reactivateMission")}
      onSecondaryAction={onSecondaryButton}
      backPath={backPath}
      referenceTag={!slim && (!!mission.request || mission.sentRequests?.length > 0) ? (
        <Stack direction="row" spacing={1} sx={{ paddingLeft: 1 }}>
          {!!mission.request ? (
            <MissionRequestReceivedChip request={mission.request} />
          ) : null}
          {mission.sentRequests?.length > 0 ? (
            <MissionRequestSentChip />
          ) : null}
        </Stack>
      ) : undefined}
      widget={<MissionActivityLog />}
      maxWidth={config.pageSize.huge}
      menuItems={menuItems}
      backButton
    >
      {
        slim && (!!mission.request || mission.sentRequests?.length > 0) ? (
          <Grid item xs={12}>
            <Stack direction="row" spacing={1}>
              {!!mission.request ? (
                <MissionRequestReceivedChip request={mission.request} />
              ) : null}
              {mission.sentRequests?.length > 0 ? (
                <MissionRequestSentChip />
              ) : null}
            </Stack>
          </Grid>
        ) : null
      }

      {props.children}
      {completeDialog ? <CompleteMissionDialog onClose={onCancelComplete} onComplete={onComplete} /> : null}
      {pdfDialog ? <PDFExportDialog onClose={onClosePdfDialog} /> : null}
      {requestDialog ? <CreateMissionRequestDialog onClose={onCloseMissionRequestDialog} onOpenRequest={onOpenCreatedRequest} /> : null}

      {
        !!cancelMissionTask ? (
          <ConfirmDialog
            task={cancelMissionTask}
            title={t("mission:cancelDialog:title")}
            description={t("mission:cancelDialog:description")}
            confirmText={t("mission:cancelDialog:confirmText")}
          />
        ) : null
      }

      {!!selectedRequestId ? (
        <MissionRequestDialog entityId={selectedRequestId} onClose={onCloseSelectedRequest} sent />
      ) : null}
    </ResponsiveSubPage>
  );
}

function getStateText(state) {
  return `mission:enums:state:${MissionEnumsLists.State[state] || 0}`;
}

function getStateColor(state) {
  switch (state) {
    case State.Draft: return "warning";
    case State.Cancelled: return "error";
    case State.Created: return "info";
    case State.Completed: return "success";
    default: return "default";
  }
}